import type { ReactNode } from 'react';

/**
 * Schlanke "Pill-Tabs" für Filter-UIs (kein voller WAI-ARIA-Tabs-Pattern,
 * weil hier kein Panel-Wechsel passiert - nur Filter-Selektion).
 *
 * Verwendung im Mapping-Wizard für "Alle / >= 90% / 70-89% / Ohne Vorschlag".
 *
 * Renders: `<div role="group">` mit Buttons. Active-State via aria-pressed.
 */

export interface TabFilterItem<T extends string> {
  id: T;
  label: ReactNode;
  count?: number;
  disabled?: boolean;
}

interface TabFilterProps<T extends string> {
  items: TabFilterItem<T>[];
  active: T;
  onChange: (id: T) => void;
  ariaLabel: string;
  className?: string;
}

export function TabFilter<T extends string>({
  items,
  active,
  onChange,
  ariaLabel,
  className,
}: TabFilterProps<T>): JSX.Element {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={['flex flex-wrap items-center gap-1', className ?? ''].join(' ')}
    >
      {items.map((it) => {
        const isActive = it.id === active;
        return (
          <button
            key={it.id}
            type="button"
            disabled={it.disabled}
            aria-pressed={isActive}
            onClick={() => !it.disabled && onChange(it.id)}
            className={[
              'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              isActive
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-surface text-muted hover:text-text',
              it.disabled ? 'cursor-not-allowed opacity-50' : '',
            ].join(' ')}
          >
            <span>{it.label}</span>
            {typeof it.count === 'number' && (
              <span
                className={[
                  'rounded-full px-2 py-0.5 text-xs',
                  isActive ? 'bg-primary text-primary-fg' : 'bg-surface-2 text-muted',
                ].join(' ')}
              >
                {it.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
